/**
 * Pure helpers for the caseload list.
 *
 * The screens (CaseloadScreen, StudentDetailScreen) load raw
 * rows from Supabase and pass them through `summarizeStudent`
 * for chip rendering. Keeping the transform pure makes the
 * UI testable without a Supabase mock.
 */
import type {
  DisorderCategory,
} from './domain';
import { labelForDisorderCategory } from './domain';

/**
 * Subset of `students` row that the caseload summary needs.
 * Defined inline (rather than importing from a generated types
 * file) so the screen stays test-friendly.
 */
export interface StudentSummary {
  id: string;
  firstName: string;
  ageYears: number | null;
  disorderCategory: DisorderCategory;
  archived: boolean;
  createdAt: string;
  lastSessionDate: string | null;
  activeGoalsCount: number;
  /**
   * Distinct `goals.area` values across the student's active
   * goals, computed server-side by the `list_students_with_counts`
   * RPC (fix #485). Mirrors the `GOAL_AREAS` enum in
   * `lib/effectiveslp/domain.ts` but is `readonly string[]` (not
   * a strict union) so an unexpected / legacy goal area from the
   * DB doesn't break the UI. The toolbar's "Filter by goal area"
   * chips match against this list — historically they matched
   * `disorderCategory`, which masked the mismatch for chips
   * whose enum happened to overlap with the disorder-category
   * set and silently broke the `voice` / `cognitive` chips
   * (#485).
   */
  goalAreas: readonly string[];
}

export interface StudentListRow {
  id: string;
  first_name: string;
  age_years: number | null;
  disorder_category: string;
  archived: boolean;
  created_at: string;
  last_session_at: string | null;
  active_goals: number;
  /** Server-rolled DISTINCT `area` values across active goals. */
  goal_areas: string[] | null;
}

function asDisorderCategory(value: string): DisorderCategory {
  switch (value) {
    case 'apraxia':
    case 'articulation':
    case 'phonology':
    case 'fluency':
    case 'receptive_language':
    case 'expressive_language':
    case 'aac':
    case 'social_pragmatic':
      return value;
    default:
      return 'articulation';
  }
}

/**
 * Convert a `StudentListRow` from Supabase into the shape the
 * caseload UI wants. Pure — no IO, no state. Each row is one
 * student card.
 *
 * `goalAreas` is the server-rolled DISTINCT `area` values across
 * the student's active goals (issue #485). The RPC returns an
 * empty array (`'{}'`) for students with no active goals, so
 * the `?? []` here is a defensive fallback for older rows / hand-
 * edited test fixtures — pre-#485 the column didn't exist.
 */
export function summarizeStudent(row: StudentListRow): StudentSummary {
  return {
    id: row.id,
    firstName: row.first_name,
    ageYears: row.age_years,
    disorderCategory: asDisorderCategory(row.disorder_category),
    archived: row.archived,
    createdAt: row.created_at,
    lastSessionDate: row.last_session_at,
    activeGoalsCount: row.active_goals,
    goalAreas: row.goal_areas ?? [],
  };
}

export function renderDisorderChip(category: DisorderCategory): string {
  return labelForDisorderCategory(category);
}
